import { type InfiniteData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getNotificationPreferences,
  getNotifications,
  getUnreadCount,
  markAllAsRead,
  markAsRead,
  updateNotificationPreferences,
} from '@/api/notifications';
import { useAuth } from '@/hooks/auth';
import type {
  NotificationFeedResponse,
  NotificationFilter,
  NotificationPreferencesResponse,
} from '@/types/notifications';

export const NOTIFICATIONS_FEED_KEY = (filter: NotificationFilter) => ['notifications', 'feed', filter] as const;

export const NOTIFICATIONS_UNREAD_COUNT_KEY = ['notifications', 'unread-count'] as const;

export const NOTIFICATION_PREFERENCES_KEY = ['notifications', 'preferences'] as const;

type MarkAsReadContext = {
  previousUnread: { count: number } | undefined;
  previousFeeds: [readonly unknown[], InfiniteData<NotificationFeedResponse> | undefined][];
};

export function useNotifications(filter: NotificationFilter, enabled: boolean) {
  const isAuthenticated = useAuth(s => s.isAuthenticated);
  return useInfiniteQuery({
    queryKey: NOTIFICATIONS_FEED_KEY(filter),
    queryFn: ({ pageParam }) =>
      getNotifications({
        cursor: pageParam as string | undefined,
        limit: 20,
        filter,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: lastPage => lastPage.next_cursor ?? undefined,
    enabled: enabled && isAuthenticated,
  });
}

export function useUnreadCount() {
  const isAuthenticated = useAuth(s => s.isAuthenticated);
  return useQuery({
    queryKey: NOTIFICATIONS_UNREAD_COUNT_KEY,
    queryFn: getUnreadCount,
    refetchInterval: 60_000,
    enabled: isAuthenticated,
  });
}

export function useNotificationPreferences() {
  const isAuthenticated = useAuth(s => s.isAuthenticated);
  return useQuery({
    queryKey: NOTIFICATION_PREFERENCES_KEY,
    queryFn: getNotificationPreferences,
    staleTime: 60 * 1000,
    enabled: isAuthenticated,
  });
}

export function useMarkAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markAsRead,
    onMutate: async (id: string): Promise<MarkAsReadContext> => {
      await queryClient.cancelQueries({ queryKey: ['notifications'] });

      const previousUnread = queryClient.getQueryData<{ count: number }>(NOTIFICATIONS_UNREAD_COUNT_KEY);
      const previousFeeds = queryClient.getQueriesData<InfiniteData<NotificationFeedResponse>>({
        queryKey: ['notifications', 'feed'],
      });

      let wasUnread = false;
      for (const [, data] of previousFeeds) {
        if (!data) continue;
        for (const page of data.pages) {
          const found = page.notifications.find(n => n.id === id);
          if (found && !found.is_read) {
            wasUnread = true;
            break;
          }
        }
        if (wasUnread) break;
      }

      queryClient.setQueriesData<InfiniteData<NotificationFeedResponse>>(
        { queryKey: ['notifications', 'feed'] },
        old => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map(page => ({
              ...page,
              notifications: page.notifications.map(n => (n.id === id ? { ...n, is_read: true } : n)),
            })),
          };
        },
      );

      if (wasUnread && previousUnread !== undefined) {
        queryClient.setQueryData(NOTIFICATIONS_UNREAD_COUNT_KEY, {
          count: Math.max(0, previousUnread.count - 1),
        });
      }

      return { previousUnread, previousFeeds };
    },
    onError: (_err, _id, context) => {
      if (!context) return;
      queryClient.setQueryData(NOTIFICATIONS_UNREAD_COUNT_KEY, context.previousUnread);
      context.previousFeeds.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },
  });
}

export function useMarkAllAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markAllAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateNotificationPreferences,
    onMutate: async body => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATION_PREFERENCES_KEY });
      const previous = queryClient.getQueryData<NotificationPreferencesResponse>(NOTIFICATION_PREFERENCES_KEY);
      if (previous) {
        let categories = previous.categories;
        if (body.categories !== undefined) {
          const byCategory = new Map(previous.categories.map(c => [c.category, c]));
          for (const patch of body.categories) {
            byCategory.set(patch.category, { category: patch.category, enabled: patch.enabled });
          }
          categories = Array.from(byCategory.values());
        }
        const next: NotificationPreferencesResponse = {
          ...previous,
          ...(body.notificationsEnabled !== undefined ? { notificationsEnabled: body.notificationsEnabled } : {}),
          ...(body.hasSeenNotificationPanel !== undefined
            ? { hasSeenNotificationPanel: body.hasSeenNotificationPanel }
            : {}),
          categories,
        };
        queryClient.setQueryData(NOTIFICATION_PREFERENCES_KEY, next);
      }
      return { previous };
    },
    onError: (_err, _body, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(NOTIFICATION_PREFERENCES_KEY, context.previous);
      }
    },
    onSuccess: data => {
      queryClient.setQueryData(NOTIFICATION_PREFERENCES_KEY, data);
    },
  });
}
